/**
 * router.options
 * @author wwl
 * @created 2024/10/11
 * @modified 2024/12/19
 */
import type { RouterOptions } from '@nuxt/schema'
import { defineAsyncComponent } from 'vue'
import { getLanguageByHost } from './utils/language-detection'

/**
 * 语义列表首段路由参数正则。
 * Vue Router 约束：自定义参数正则里不要使用 (?...) 非捕获组（会报 Unterminated group）；末尾不要用 `/?`，否则整条路由无法匹配如 /g-16；
 * `[^/]` 易触发解析问题，这里用 [\w.-]+ 约束单段 slug。
 */
const fairSemanticPrimaryParam =
  'n-intl|n-cn|g-[\\w.-]+|i-[\\w.-]+|c-[\\w.-]+|r-[\\w.-]+|v-[\\w.-]+|t-[\\w.-]+|S-[\\w.-]+|s-[\\w.-]+'

/** 语义展会列表路由：英文 enRoutes 已注册同 path 时不得通过 zhOnlyRoutes 回退到中文 index.vue */
function isFairSemanticListRoute(route: { path: string; meta?: Record<string, unknown> }): boolean {
  if (route.meta?.fairSemanticList) return true
  if (route.path === '/all' || route.path.startsWith('/all/')) return true
  if (route.path.startsWith('/p-:page')) return true
  if (route.path.startsWith('/m-:month')) return true
  return false
}

/** 中英文共用的语义列表路由（legacy /exhibition* 由调用方单独注册） */
function fairListSemanticRoutes(listComponent: () => Promise<{ default: unknown }>) {
  const component = defineAsyncComponent(listComponent)
  return [
    {
      name: '展会-all',
      path: '/all',
      meta: { fairSemanticList: true },
      component
    },
    {
      name: '展会列表-all-扩展',
      path: '/all/:rest(.+)',
      meta: { fairSemanticList: true },
      component
    },
    {
      name: '展会列表-全国分页-兼容',
      path: '/p-:page(\\d+)',
      redirect: (to: { params: Record<string, unknown>; query: Record<string, unknown>; hash: string }) => ({
        path: `/all/p-${String(to.params.page ?? '1')}`,
        query: to.query,
        hash: to.hash
      })
    },
    {
      name: '展会列表-全国月份-兼容',
      path: '/m-:month(\\d+)',
      redirect: (to: { params: Record<string, unknown>; query: Record<string, unknown>; hash: string }) => ({
        path: `/all/m-${String(to.params.month ?? '1')}`,
        query: to.query,
        hash: to.hash
      })
    },
    {
      name: '展会列表-全国月份分页-兼容',
      path: '/m-:month(\\d+)/p-:page(\\d+)',
      redirect: (to: { params: Record<string, unknown>; query: Record<string, unknown>; hash: string }) => {
        const m = String(to.params.month ?? '1')
        const p = Math.max(1, Math.floor(Number(to.params.page)) || 1)
        return {
          path: `/all/m-${m}/p-${p}`,
          query: to.query,
          hash: to.hash
        }
      }
    },
    {
      name: '展会列表-语义-单段',
      path: `/:primary(${fairSemanticPrimaryParam})`,
      meta: { fairSemanticList: true },
      component
    },
    {
      name: '展会列表-语义-多段',
      path: `/:primary(${fairSemanticPrimaryParam})/:rest(.+)`,
      meta: { fairSemanticList: true },
      component
    }
  ]
}
/** URL改造：目录式展会详情及 5 类嵌套子页路由（segment = 数字 ID 或语义 slug，含 Unicode / percent-encoded） */

/** 英文站新详情路由（旧 .html 路由保留，由 middleware 301） */
const enExhibitionDetailNewRoutes = [
  // 详情主页 /exhibition/:segment/
  {
    name: 'exhibitionDetailNew',
    path: `/exhibition/:segment/`,
    component: defineAsyncComponent(() => import('~/pages/fair/detail/detail-en.vue'))
  },
  // 门票子页 /exhibition/:segment/ticket/
  {
    name: 'exhibitionTicketNew',
    path: `/exhibition/:segment/ticket/`,
    component: defineAsyncComponent(() => import('~/pages/fair/ticket/ticket.vue'))
  },
  // 展位子页 /exhibition/:segment/apply/
  {
    name: 'exhibitionApplyNew',
    path: `/exhibition/:segment/apply/`,
    component: defineAsyncComponent(() => import('~/pages/booth/index.vue'))
  },
  // 会刊子页 /exhibition/:segment/periodical/
  {
    name: 'exhibitionPeriodicalNew',
    path: `/exhibition/:segment/periodical/`,
    component: defineAsyncComponent(() => import('~/pages/fair/periodical_new.vue'))
  },
  // 社区子页 /exhibition/:segment/community/
  {
    name: 'exhibitionCommunityNew',
    path: `/exhibition/:segment/community/`,
    component: defineAsyncComponent(() => import('~/pages/community/index.vue'))
  },
  // 资讯子页 /exhibition/:segment/expoinfo/
  {
    name: 'exhibitionExpoinfoNew',
    path: `/exhibition/:segment/expoinfo/`,
    component: defineAsyncComponent(() => import('~/pages/exhDetailInf/index.vue'))
  }
]

/** 中文站新详情路由（结构与英文站一致） */
const zhExhibitionDetailNewRoutes = [
  // 详情主页
  {
    name: '展会详情-新',
    path: `/exhibition/:segment/`,
    component: defineAsyncComponent(() => import('~/pages/fair/detail/detail.vue'))
  },
  // 门票子页
  {
    name: '展会门票-新',
    path: `/exhibition/:segment/ticket/`,
    component: defineAsyncComponent(() => import('~/pages/fair/ticket/ticket.vue'))
  },
  // 展位子页
  {
    name: '展会申请-新',
    path: `/exhibition/:segment/apply/`,
    component: defineAsyncComponent(() => import('~/pages/booth/index.vue'))
  },
  // 会刊子页
  {
    name: '展会会刊-新',
    path: `/exhibition/:segment/periodical/`,
    component: defineAsyncComponent(() => import('~/pages/fair/periodical_new.vue'))
  },
  // 社区子页
  {
    name: '用户社区-新',
    path: `/exhibition/:segment/community/`,
    component: defineAsyncComponent(() => import('~/pages/community/index.vue'))
  },
  // 资讯子页
  {
    name: '展会资讯-新',
    path: `/exhibition/:segment/expoinfo/`,
    component: defineAsyncComponent(() => import('~/pages/exhDetailInf/index.vue'))
  }
]

const enRoutes = [
  {
    name: 'Home',
    path: '/',
    component: defineAsyncComponent(() => import('~/pages/home/Home-en.vue'))
  },
  {
    name: '展会-en',
    path: '/exhibition/',
    component: defineAsyncComponent(() => import('~/pages/fair/list/index-en.vue'))
  },
  {
    name: '国际展会',
    path: '/exhibition-:f-:s-:g-:p-:c-:t-:page/',
    component: defineAsyncComponent(() => import('~/pages/fair/list/index-en.vue'))
  },
  ...fairListSemanticRoutes(() => import('~/pages/fair/list/index-en.vue')),
  {
    name: 'exhibitionDetail',
    path: '/exhibition/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/detail/detail-en.vue'))
  },
  // URL改造：英文站目录式详情 + 5 类子页（旧 .html 路由保留在上方）
  ...enExhibitionDetailNewRoutes,
  {
    name: '展会门票2',
    path: '/ticket/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/ticket/ticket.vue'))
  },
  {
    name: '展会资讯',
    path: '/information/',
    component: defineAsyncComponent(() => import('~/pages/news/list/News-en.vue'))
  },
  {
    name: '展会资讯-分页',
    path: '/information-:t-:i-:page/',
    component: defineAsyncComponent(() => import('~/pages/news/list/News-en.vue'))
  },
  {
    name: '资讯详情',
    path: '/information/:id.html',
    component: defineAsyncComponent(() => import('~/pages/news/details/Details-en.vue'))
  },
  {
    name: '资讯详情2',
    path: '/exponews/:id.html',
    component: defineAsyncComponent(() => import('~/pages/news/details/Details-en.vue'))
  },
  {
    name: '会展中心-',
    path: '/pavilion-:f-:s-:g-:p-:c-:t-:page/',
    component: defineAsyncComponent(() => import('~/pages/center/Center-en.vue'))
  },
  {
    name: '会展中心',
    path: '/pavilion/',
    component: defineAsyncComponent(() => import('~/pages/center/Center-en.vue'))
  },
  {
    name: '会展中心详情',
    path: '/pavilion/:id.html',
    component: defineAsyncComponent(() => import('~/pages/center/Details-en.vue'))
  },
  {
    name: '展会合作',
    path: '/cooperation.html',
    component: defineAsyncComponent(() => import('~/pages/cooperation/Cooperation.vue'))
  },
  {
    name: '支付状态',
    path: '/result/:status',
    component: defineAsyncComponent(() => import('~/pages/pay/Result.vue'))
  },
  {
    name: 'Login',
    path: '/login',
    component: defineAsyncComponent(() => import('~/pages/login/index.vue'))
  },
  {
    name: 'User Center',
    path: '/expouser/:id.html',
    component: defineAsyncComponent(() => import('~/pages/expouser/index-en.vue'))
  },
  {
    name: 'User Management Center',
    path: '/manager',
    component: defineAsyncComponent(() => import('~/pages/manager/index.vue')),
    meta: {
      isLogin: true
    },
    children: [
      {
        name: 'Change Password',
        path: '/manager/update-pwd',
        component: defineAsyncComponent(() => import('~/pages/manager/update-pwd.vue'))
      },
      {
        name: 'Enterprise Authentication',
        path: '/manager/auth',
        component: defineAsyncComponent(() => import('~/pages/manager/auth.vue'))
      },
      {
        name: 'My Exhibitions',
        path: '/manager/fair-my',
        component: defineAsyncComponent(() => import('~/pages/manager/fair-my.vue'))
      },
      {
        name: 'My Tickets',
        path: '/manager/ticket-my',
        component: defineAsyncComponent(() => import('~/pages/manager/ticket-my.vue')),
        meta: {
          isLogin: true
        }
      },
      {
        name: 'Visitor Management',
        path: '/manager/visitor',
        component: defineAsyncComponent(() => import('~/pages/manager/visitor.vue'))
      },
      {
        name: 'Update Info',
        path: '/manager/update-info',
        component: defineAsyncComponent(() => import('~/pages/manager/update-info.vue'))
      },
      {
        name: 'My Visa',
        path: '/manager/visa-my',
        component: defineAsyncComponent(() => import('~/pages/manager/visa.vue'))
      },
      // 新增的manager模块页面
      {
        name: 'Exhibition Reminders',
        path: '/manager/fair-remind',
        component: defineAsyncComponent(() => import('~/pages/manager/fair-remind.vue'))
      },
      {
        name: 'Followed Exhibitions',
        path: '/manager/fair-follow',
        component: defineAsyncComponent(() => import('~/pages/manager/fair-follow.vue'))
      },
      {
        name: 'Followed News',
        path: '/manager/news-follow',
        component: defineAsyncComponent(() => import('~/pages/manager/news-follow.vue'))
      },
      {
        name: 'My News',
        path: '/manager/news-my',
        component: defineAsyncComponent(() => import('~/pages/manager/news-my.vue'))
      },
      {
        name: 'Followed Users',
        path: '/manager/usersFollow',
        component: defineAsyncComponent(() => import('~/pages/manager/users-follow.vue'))
      },
      {
        name: 'My Fans',
        path: '/manager/usersFans',
        component: defineAsyncComponent(() => import('~/pages/manager/users-fans.vue'))
      },
      {
        name: 'Followed Industries',
        path: '/manager/industry-my',
        component: defineAsyncComponent(() => import('~/pages/manager/industry-my.vue'))
      },
      {
        name: 'Visitor Edit',
        path: '/manager/visitor-edit/:id?',
        component: defineAsyncComponent(() => import('~/pages/manager/visitor-edit.vue'))
      },
      {
        name: 'My Periodicals',
        path: '/manager/periodical-my',
        component: defineAsyncComponent(() => import('~/pages/manager/periodical-my.vue'))
      },
      {
        name: 'Partner Visitors',
        path: '/manager/partner/visitors',
        component: defineAsyncComponent(() => import('~/pages/partner/visitors.vue')),
        meta: { isLogin: true }
      },
      {
        name: 'Partner Performance',
        path: '/manager/partner/performance',
        component: defineAsyncComponent(() => import('~/pages/partner/performance.vue')),
        meta: { isLogin: true }
      },
      {
        name: 'Partner Ledger',
        path: '/manager/partner/ledger',
        component: defineAsyncComponent(() => import('~/pages/partner/ledger.vue')),
        meta: { isLogin: true }
      },
      {
        name: 'Partner Withdraw',
        path: '/manager/partner/withdraw',
        component: defineAsyncComponent(() => import('~/pages/partner/withdraw.vue')),
        meta: { isLogin: true }
      },
      {
        name: 'Partner Distribution Register',
        path: '/manager/partner/register',
        component: defineAsyncComponent(() => import('~/pages/partner/register.vue')),
        meta: { isLogin: true }
      },
      {
        name: 'Ticket order periodical',
        path: '/manager/ticket-order-periodical/:exhibitionId/:orderNo',
        component: defineAsyncComponent(() => import('~/pages/manager/ticket-order-periodical.vue'))
      }
    ]
  },
  { path: '/partner', redirect: '/manager/partner/visitors' },
  { path: '/partner/visitors', redirect: '/manager/partner/visitors' },
  { path: '/partner/performance', redirect: '/manager/partner/performance' },
  { path: '/partner/ledger', redirect: '/manager/partner/ledger' },
  { path: '/partner/withdraw', redirect: '/manager/partner/withdraw' },
  {
    name: 'Visa Payment Result',
    path: '/visa-result/:status',
    component: defineAsyncComponent(() => import('~/pages/visa/[status].vue'))
  },
  {
    name: 'Special Topic',
    path: '/special/:id.html',
    component: defineAsyncComponent(() => import('~/pages/special/index.vue'))
  },
  {
    name: 'Exhibition Info',
    path: '/expoinfo/:id.html',
    component: defineAsyncComponent(() => import('~/pages/exhDetailInf/index.vue'))
  },
  {
    name: 'Community',
    path: '/community/:id.html',
    component: defineAsyncComponent(() => import('~/pages/community/index.vue'))
  },
  {
    name: 'Exhibitors List',
    path: '/exhibitors/:id.html',
    // component: () => import('~/pages/company/list.vue')
    component: defineAsyncComponent(() => import('~/pages/exhibitors/[id].vue'))
  },
  {
    name: 'Company Detail',
    path: '/company/:id.html',
    component: defineAsyncComponent(() => import('~//pages/company/[id].vue'))
  },
  {
    name: 'Periodical Detail',
    path: '/periodical/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/periodical_new.vue'))
    // component: defineAsyncComponent(() => import('~/pages/fair/periodical_new.vue'))
  },
  {
    name: 'Exhibition Periodical Detail',
    path: '/exhibition/periodical/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/periodical.vue'))
  },
  {
    name: 'Terms of Service',
    path: '/terms',
    component: defineAsyncComponent(() => import('~/pages/terms/index.vue'))
  }
]

const zhRoutes = [
  {
    name: '首页',
    path: '/',
    component: defineAsyncComponent(() => import('~/pages/home/Home.vue'))
  },
  // {
  //   name: '首页seo',
  //   path: '/seo',
  //   component: defineAsyncComponent(() => import('~/pages/home/Home.vue'))
  // },
  {
    name: '展会列表限流',
    path: '/queue/',
    component: defineAsyncComponent(() => import('~/pages/fair/list/queue.vue'))
  },
  /** 中文站旧列表 `/exhibition`、`/exhibition-{f}-{s}-…`：404→301 见 server/middleware/00-fairListLegacyGuardAndRedirect.ts + fairListLegacyRedirect.global.ts */
  {
    name: '展会-legacy',
    path: '/exhibition/',
    component: defineAsyncComponent(() => import('~/pages/fair/list/index.vue'))
  },
  {
    name: '国际展会',
    path: '/exhibition-:f-:s-:g-:p-:c-:t-:page/',
    component: defineAsyncComponent(() => import('~/pages/fair/list/index.vue'))
  },
  ...fairListSemanticRoutes(() => import('~/pages/fair/list/index.vue')),
  //
  {
    name: '展会资讯',
    path: '/information/',
    component: defineAsyncComponent(() => import('~/pages/news/list/News.vue'))
  },
  {
    name: '展会资讯-分页',
    path: '/information-:t-:i-:page/',
    component: defineAsyncComponent(() => import('~/pages/news/list/News.vue'))
  },
  // {
  //   name: '展会资讯seo',
  //   path: '/seo/information/',
  //   component: defineAsyncComponent(() => import('~/pages/news/list/News.vue'))
  // },
  // {
  //   name: '展会资讯-分页seo',
  //   path: '/seo/information-:t-:i-:page/',
  //   component: defineAsyncComponent(() => import('~/pages/news/list/News.vue'))
  // },
  {
    name: '资讯详情',
    path: '/information/:id.html',
    component: defineAsyncComponent(() => import('~/pages/news/details/Details.vue'))
  },

  {
    name: '资讯详情2',
    path: '/exponews/:id.html',
    component: defineAsyncComponent(() => import('~/pages/news/details/Details.vue'))
  },

  {
    name: '会展中心-',
    path: '/pavilion-:f-:s-:g-:p-:c-:t-:page/',
    component: defineAsyncComponent(() => import('~/pages/center/Center.vue'))
  },
  {
    name: '会展中心',
    path: '/pavilion/',
    component: defineAsyncComponent(() => import('~/pages/center/Center.vue'))
  },
  {
    name: '会展中心详情',
    path: '/pavilion/:id.html',
    component: defineAsyncComponent(() => import('~/pages/center/Details.vue'))
  },
  {
    name: '展会合作',
    path: '/cooperation.html',
    component: defineAsyncComponent(() => import('~/pages/cooperation/Cooperation.vue'))
  },
  {
    name: 'exhibitionDetail',
    path: '/exhibition/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/detail/detail.vue'))
  },
  // URL改造：中文站目录式详情 + 5 类子页（旧 .html 路由保留在上方）
  ...zhExhibitionDetailNewRoutes,

  {
    name: '展会门票1',
    path: '/exhibition/ticket/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/ticket/index.vue'))
  }, // 门票页面旧的路由
  {
    name: '展会门票2',
    path: '/ticket/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/ticket/ticket.vue'))
  },
  {
    name: '支付状态',
    path: '/result/:status',
    component: defineAsyncComponent(() => import('~/pages/pay/Result.vue'))
  },
  // 其他
  {
    name: 'sitemap_index',
    path: '/sitemap_index/',
    component: defineAsyncComponent(() => import('~/pages/sitemap/letter.vue'))
  },
  {
    name: 'sitemap_',
    path: '/sitemap_:letter/',
    component: defineAsyncComponent(() => import('~/pages/sitemap/letterInfo.vue'))
  },
  {
    name: 'sitemap',
    path: '/sitemap/',
    component: defineAsyncComponent(() => import('~/pages/sitemap/index.vue'))
  },
  {
    name: 'health',
    path: '/system/index',
    component: defineAsyncComponent(() => import('~/pages/system/index.vue'))
  },
  {
    name: 'system-error',
    path: '/system/error',
    component: defineAsyncComponent(() => import('~/pages/system/error.vue'))
  },
  {
    name: '关于我们',
    path: '/about/:id.html',
    component: defineAsyncComponent(() => import('~/pages/about/index-nuxt3.vue'))
  },
  {
    name: '用户中心',
    path: '/expouser/:id.html',
    component: defineAsyncComponent(() => import('~/pages/expouser/index.vue'))
  },
  {
    name: '展会百科首页',
    path: '/wiki',
    component: defineAsyncComponent(() => import('~/pages/wiki/index.vue'))
  },
  {
    name: '展会百科列表2',
    path: '/wiki-:s-:page/',
    component: defineAsyncComponent(() => import('~/pages/wiki/list.vue'))
  },
  {
    name: '展会百科详情页',
    path: '/wiki/:id-:a-:h-:aid.html',
    component: defineAsyncComponent(() => import('~/pages/wiki/detail.vue'))
  },
  {
    name: '展团行程',
    path: '/exhibition/trip/:id.html',
    component: defineAsyncComponent(() => import('~/pages/trip/index.vue'))
  },
  {
    name: '展会申请',
    path: '/exhibition/apply/:id.html',
    component: defineAsyncComponent(() => import('~/pages/booth/index.vue'))
    // meta: {
    //   isLogin: true,
    // },
  },
  {
    name: '登录',
    path: '/login',
    component: defineAsyncComponent(() => import('~/pages/login/index.vue'))
  },
  {
    name: '用户管理中心',
    path: '/manager',
    component: defineAsyncComponent(() => import('~/pages/manager/index.vue')),
    meta: {
      isLogin: true
    },
    children: [
      {
        name: '修改密码',
        path: '/manager/update-pwd',
        component: defineAsyncComponent(() => import('~/pages/manager/update-pwd.vue'))
      },
      {
        name: '企业认证',
        path: '/manager/auth',
        component: defineAsyncComponent(() => import('~/pages/manager/auth.vue'))
      },
      {
        name: '我的展会',
        path: '/manager/fair-my',
        component: defineAsyncComponent(() => import('~/pages/manager/fair-my.vue'))
      },
      {
        name: '我的门票',
        path: '/manager/ticket-my',
        component: defineAsyncComponent(() => import('~/pages/manager/ticket-my.vue'))
      },
      {
        name: '访客管理',
        path: '/manager/visitor',
        component: defineAsyncComponent(() => import('~/pages/manager/visitor.vue'))
      },
      {
        name: '修改资料',
        path: '/manager/update-info',
        component: defineAsyncComponent(() => import('~/pages/manager/update-info.vue'))
      },
      // {
      //   name: '我的签证',
      //   path: '/manager/visa-my',
      //   component: defineAsyncComponent(() => import('~/pages/manager/visa.vue'))
      // },
      // 新增的manager模块页面
      {
        name: '展会提醒',
        path: '/manager/fair-remind',
        component: defineAsyncComponent(() => import('~/pages/manager/fair-remind.vue'))
      },
      {
        name: '关注的展会',
        path: '/manager/fair-follow',
        component: defineAsyncComponent(() => import('~/pages/manager/fair-follow.vue'))
      },
      {
        name: '关注公司',
        path: '/manager/compony-follow',
        component: defineAsyncComponent(() => import('~/pages/manager/compony-follow.vue'))
      },
      {
        name: '关注的资讯',
        path: '/manager/news-follow',
        component: defineAsyncComponent(() => import('~/pages/manager/news-follow.vue'))
      },
      {
        name: '我的资讯',
        path: '/manager/news-my',
        component: defineAsyncComponent(() => import('~/pages/manager/news-my.vue'))
      },
      {
        name: '关注的用户',
        path: '/manager/usersFollow',
        component: defineAsyncComponent(() => import('~/pages/manager/users-follow.vue'))
      },
      {
        name: '我的粉丝',
        path: '/manager/usersFans',
        component: defineAsyncComponent(() => import('~/pages/manager/users-fans.vue'))
      },
      {
        name: '关注的行业',
        path: '/manager/industry-my',
        component: defineAsyncComponent(() => import('~/pages/manager/industry-my.vue'))
      },
      {
        name: '访客编辑',
        path: '/manager/visitor-edit/:id?',
        component: defineAsyncComponent(() => import('~/pages/manager/visitor-edit.vue'))
      },
      {
        name: '我的会刊',
        path: '/manager/periodical-my',
        component: defineAsyncComponent(() => import('~/pages/manager/periodical-my.vue'))
      },
      {
        name: '伙伴访客',
        path: '/manager/partner/visitors',
        component: defineAsyncComponent(() => import('~/pages/partner/visitors.vue')),
        meta: { isLogin: true }
      },
      {
        name: '伙伴业绩',
        path: '/manager/partner/performance',
        component: defineAsyncComponent(() => import('~/pages/partner/performance.vue')),
        meta: { isLogin: true }
      },
      {
        name: '伙伴分成流水',
        path: '/manager/partner/ledger',
        component: defineAsyncComponent(() => import('~/pages/partner/ledger.vue')),
        meta: { isLogin: true }
      },
      {
        name: '伙伴提现',
        path: '/manager/partner/withdraw',
        component: defineAsyncComponent(() => import('~/pages/partner/withdraw.vue')),
        meta: { isLogin: true }
      },
      {
        name: '分销注册',
        path: '/manager/partner/register',
        component: defineAsyncComponent(() => import('~/pages/partner/register.vue')),
        meta: { isLogin: true }
      },
      {
        name: '订单电子会刊',
        path: '/manager/ticket-order-periodical/:exhibitionId/:orderNo',
        component: defineAsyncComponent(() => import('~/pages/manager/ticket-order-periodical.vue'))
      }
    ]
  },
  { path: '/partner', redirect: '/manager/partner/visitors' },
  { path: '/partner/visitors', redirect: '/manager/partner/visitors' },
  { path: '/partner/performance', redirect: '/manager/partner/performance' },
  { path: '/partner/ledger', redirect: '/manager/partner/ledger' },
  { path: '/partner/withdraw', redirect: '/manager/partner/withdraw' },
  {
    name: '专题页面',
    path: '/special/:id.html',
    component: defineAsyncComponent(() => import('~/pages/special/index.vue'))
  },
  {
    name: '展会资讯2',
    path: '/expoinfo/:id.html',
    component: defineAsyncComponent(() => import('~/pages/exhDetailInf/index.vue'))
  },
  {
    name: '用户社区',
    path: '/community/:id.html',
    component: defineAsyncComponent(() => import('~/pages/community/index.vue'))
  },
  {
    name: '参展商名录',
    path: '/exhibitors/:id.html',
    component: defineAsyncComponent(() => import('~/pages/exhibitors/[id].vue'))
  },
  {
    name: '公司详情',
    path: '/company/:id.html',
    component: defineAsyncComponent(() => import('~/pages/company/[id].vue'))
  },
  {
    name: '签证列表首页',
    path: '/visa',
    component: defineAsyncComponent(() => import('~/pages/visa/visa-[visaAreaId]-[areaId]-[type]-[bizType]-[page].vue'))
  },
  {
    name: '签证列表',
    path: '/visa-:visaAreaId-:areaId-:type-:bizType-:page/',
    component: defineAsyncComponent(() => import('~/pages/visa/visa-[visaAreaId]-[areaId]-[type]-[bizType]-[page].vue'))
  },
  {
    name: '签证详情',
    path: '/visa/:id.html',
    component: defineAsyncComponent(() => import('~/pages/visa/[id].vue'))
  },
  {
    name: '签证订单提交',
    path: '/visa/submitOrder',
    component: defineAsyncComponent(() => import('~/pages/visa/submitOrder.vue'))
  },
  // 展商名录详情和会刊详情共用一个路由，/periodical/:id.html路由有seo抓取所以保留
  {
    name: '展商名录详情',
    path: '/periodical/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/periodical_new.vue'))
    // component: defineAsyncComponent(() => import('~/pages/fair/periodical_new.vue'))
  },
  {
    name: '展会会刊详情',
    path: '/exhibition/periodical/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/periodical.vue'))
  },
  {
    name: '服务协议',
    path: '/terms',
    component: defineAsyncComponent(() => import('~/pages/terms/index.vue'))
  },
  {
    name: '选择支付方式',
    path: '/payment',
    component: defineAsyncComponent(() => import('~/pages/visa/payment-new.vue'))
  },
  {
    name: '签证支付结果',
    path: '/visa-result/:status',
    component: defineAsyncComponent(() => import('~/pages/visa/payResult.vue'))
  },
  // 消息中心：嵌入 MessageChatPanel，与全局客服弹框共用聊天逻辑；需登录 middleware
  {
    name: '消息中心',
    path: '/messages',
    component: defineAsyncComponent(() => import('~/pages/messages/index.vue'))
  }
]

// https://router.vuejs.org/api/interfaces/routeroptions.html
export default <RouterOptions>{
  routes: _routes => {
    // 在服务端，尝试获取请求的 host 信息
    let host = ''

    if (process.server) {
      try {
        // 尝试通过多种方式获取 host 信息
        const nuxtApp = tryUseNuxtApp()
        // 优先从 header 中获取 host
        if (nuxtApp?.ssrContext?.event?.headers) {
          const headers = nuxtApp.ssrContext.event.headers
          host = headers.get('x-forwarded-host') || headers.get('host') || headers.get('Host') || ''
        } else if (nuxtApp?.ssrContext?.event?.node?.req?.headers?.host) {
          host = nuxtApp.ssrContext.event.node.req.headers.host as string
        }

        // 如果还是获取不到，尝试从环境变量或其他来源获取
        if (!host) {
          // 可以从环境变量中获取默认域名
          host = process.env.HOST || 'www.jufair.com'
        }
      } catch (error) {
        // 如果获取失败，使用默认值
        console.warn('Failed to get host in router.options, using default:', error)
        host = 'www.jufair.com'
      }
    } else {
      // 客户端可以通过 window.location.host 获取
      if (typeof window !== 'undefined') {
        host = window.location.host
      }
    }

    // 根据 host 判断语言
    const language = getLanguageByHost(host)

    // 🔧 英文域名时，如果英文路由找不到，回退到中文路由（排除语义列表路由，避免落到中文 index.vue）
    if (language === 'en') {
      const enPaths = new Set(enRoutes.map(r => r.path))
      const zhOnlyRoutes = zhRoutes.filter(
        r => !enPaths.has(r.path) && !isFairSemanticListRoute(r)
      )
      return [...enRoutes, ...zhOnlyRoutes]
    } else {
      // 中文域名只使用中文路由
      //console.log(`[Router] 🇨🇳 中文域名使用中文路由: ${host}`)
      return zhRoutes
    }
  },
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
}
